﻿// #region: Class definition *********************************************************************/
/**
 * Handles all logic for a tabstrip.
 * A tabstrip is defined in HTML:
 * 
 * <div id="tabstrip" class="tabstrip">
 *   <ul>
 *       <li>
 *          <input type="radio" name="tabs" id="tab-id" data-page="url-to-load-in-tab-id-page" /><label for="tab-id">...tab label</label>
 *       </li>
 *       ...more list items with a radio button with name="tabs" and 'data-page' for the url to load.
 *   </ul>
 *   <div class="frames">
 *       <iframe id="tab-id-page"></iframe>
 *       ...more frames where the (page) id should match the 'tab-id' of the radio button in a list item.
 *    </div>
 * </div>
 * 
 * Depends on the core and event-broadcaster class.
 */
class tabstrip {

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor(defaultTab, callback) {

        // A callback can be provided that is called on tab click.
        this.callback = callback;

        // Is selecting a new tab active.
        this.isSwitchingTab = false;

        // All tabs present in this tabstrip.
        this.tabs = [...document.querySelectorAll(".tabstrip input[name=tabs]")];

        // Add the events to the tabstrip instance.
        this.tabs.forEach((tab) => {
            // Add the click event on the radio (tab) to (re)load the url.
            // We use 'click' as the 'change' event will not fire when we set the selected radio to checked from script.
            // We can however reliably fire the click event.
            tab.addEventListener("click", (e) => {
                if (this.callback && !this.callback(e, tab)) {
                    return false;
                }
                
                // Select active tab.
                this.handleTabChange(tab.id);
            }, { capture: false });
        });

        // React to url change.
        top.window.addEventListener("popstate", () => {
            var urlTab = isEmbed ? core.getEmbedUrl().searchParams.get("tab") : core.getQueryParameter("tab");

            this.selectTab(urlTab || defaultTab);
        });

        // Set initial tab.
        this.selectTab(core.getQueryParameter("tab", window) || defaultTab);
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string}                        The string representation of this class.
     */
    toString() {
        return "[tabstrip]";
    }

    static toString() {
        return "[tabstrip]";
    }

    /** 
     * Identifies the version.
     * @returns {string}                        The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Enables all tabs.
     */
    enableAllTabs() {
        this.tabs.forEach((tab) => {
            tab.removeAttribute("disabled");
        });
    }

    /**
     * Get the active tab.
     * @returns                                  The currently active tab or the first in the list.
     */
    getActiveTab() {
        return document.querySelector(".frames > iframe.active") ?? this.tabs[0];
    }

    /**
     * Performs the action needed to switch tabs.
     * @param {String} newTabId                  The id for the new tab selected.
     */
    handleTabChange(newTabId) {
        const activeTab = this.getActiveTab();

        const url = document.querySelector(`#${newTabId}`).dataset.page;
        const iframe = document.querySelector(`#${newTabId}-page`);

        // So no tab is active at all.
        activeTab.classList.remove("active");

        // Make this tab active.
        iframe.classList.add("active");
        document.querySelector(`#${newTabId}`).checked = true;

        // Set new URL if needed.
        if (!iframe.contentWindow?.location.href.includes(url)) {
            var loadHandler = function () {
                eventBroadcaster.broadcast({ detail: { show: false }, message: broadcasts.LOCK_SCREEN });
                
                this.switchTabUrl(top.window, newTabId, iframe);
                this.isSwitchingTab = false;

                iframe.removeEventListener("load", loadHandler);
            }.bind(this);

            iframe.addEventListener("load", loadHandler, { capture: false });

            // TODO only lock frames container
            eventBroadcaster.broadcast({ detail: { show: true }, message: broadcasts.LOCK_SCREEN });
            iframe.src = baseUrl + url;
        }
        else {
            this.switchTabUrl(top.window, newTabId, iframe);
            this.isSwitchingTab = false;
        }
    }

    /**
     * In some cases the current tab may need to be reloaded.
     */
    reloadActiveTab() {
        const currentTab = document.querySelector("iframe.active");

        currentTab.addEventListener("load", function handler() {
            eventBroadcaster.broadcast({ detail: { show: false }, message: broadcasts.LOCK_SCREEN });
            this.removeEventListener("load", handler);
        }, { capture: false });

        eventBroadcaster.broadcast({ detail: { show: true }, message: broadcasts.LOCK_SCREEN });
        currentTab.contentWindow.location.reload(true);
    }

    /**
     * Changing/saving any of the content in any of the tabs (might) need a reload to reflect the change.
     * @@param {String} tabId                    The id of the tab to reload.
     */
    reloadTab(tabId) {
        document.querySelector(`#${tabId}-page`)?.contentWindow.location.reload(true);
    }

    /**
     * Changing/saving any of the content in any of the tabs (might) need a reload to reflect the change.
     */
    reloadTabs() {
        [...document.querySelectorAll("iframe:not(.active)")].forEach((frame) => {
            if (frame.src) {
                frame.contentWindow.location.reload(true);
            }
        });
    }

    /**
     * Performs the action needed to undo the switching of the tabs, make the original tab button active again.
     * @param {String} newtab                    The id for the new tab selected.
     */
    resetTabChange(newTab) {
        document.querySelector(`#${this.getActiveTab().id.replace("-page", "")}`).checked = true;
        newTab.checked = false;
    }

    /**
     * Select the tab (by its id).
     * Also called form the outside (on push state change?).
     * @@param {Integer} id                      The tab to show.
     */
    selectTab(id) {
        if (!id) {
            return;
        }

        const tab = document.querySelector("#" + id);

        if (tab && !tab.checked) {
            this.isSwitchingTab = true;

            tab.disabled = false;
            tab.click();
        }
    }

    /**
     * Update tab info om the document and browsing history.
     * @param {Window} mainWindow                The window reference to use.
     * @param {String} id                        The identifier for the tab to select.
     * @param {HTMLIFrameElement} iframe         The active selected iFrame.
     */
    switchTabUrl(mainWindow, id, iframe) {
        const newTabUrl = core.setQueryParameter(window.location.href, "tab", id);

        // Using replaceState to fix history navigation to specific tab.
        window.history.replaceState({ path: newTabUrl }, null, newTabUrl);

        if (!this.isSwitchingTab) {
            // Using pushState as a tabstrip is nested iFrames in a main iFrame.
            // Because of the nesting there is no history entry created in the parent page.
            if (isEmbed) {
                const embedUrl = core.removeQueryParameter(newTabUrl, "embed");
                core.setEmbedUrl(embedUrl, true);
            } else {
                const newUrl = core.setQueryParameter(mainWindow.location.href, "tab", id);
                mainWindow.history.pushState({ path: newTabUrl }, null, newUrl);
            }
        }

        topWindow.document.title = resources.consoleTitle
            .replace("{BrandName}", brandName)
            .replace("{ClientBrandName}", clientBrandName)
            .replace("{Page}", `: ${iframe.contentWindow.document.title}`);
    }
    // #endregion ********************************************************************************/

};
// #endregion ************************************************************************************/